import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { fetchDashboard, fetchEvents, getDailySummary, getWeeklySummary, getMonthlySummary, updateEventStatus, updateEvent } from '../lib/api';
import { 
  format, isToday, isSameMonth, isSameDay,
  startOfWeek, endOfWeek, startOfMonth, endOfMonth,
  eachDayOfInterval, addMonths, subMonths, addWeeks, subWeeks,
  startOfDay, endOfDay, addDays, subDays, parseISO
} from 'date-fns';
import { 
  Clock, MapPin, Loader2, 
  ChevronLeft, ChevronRight, Sparkles, RefreshCw,
  CalendarDays, Users, Calendar, CalendarRange, CalendarCheck,
  ChevronDown, Check, X, CheckCircle2, XCircle, Edit2
} from 'lucide-react';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';

// Floating Widget Component
const FloatingWidget = ({ children, onClick, className = '' }) => (
  <div
    onClick={onClick}
    className={`relative ${onClick ? 'cursor-pointer active:scale-[0.98]' : ''} transition-transform ${className}`}
    style={{
      background: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(40px)',
      WebkitBackdropFilter: 'blur(40px)',
      borderRadius: '16px',
      boxShadow: '0 2px 16px -4px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.03)',
    }}
  >
    {children}
  </div>
);

// Time Filter Chip
const TimeChip = ({ icon: Icon, label, isActive, onClick }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all whitespace-nowrap ${
      isActive 
        ? 'bg-gray-900 text-white shadow-md' 
        : 'bg-white text-gray-700 border border-gray-200 hover:border-gray-300'
    }`}
  >
    <Icon className="h-4 w-4" />
    {label}
  </button>
);

// Event Detail Modal
const EventDetailModal = ({ event, open, onClose, onStatusUpdate, onEdit }) => {
  const [updating, setUpdating] = useState(false);
  
  if (!event) return null;
  
  const handleStatusChange = async (status) => {
    setUpdating(true);
    try {
      await onStatusUpdate(event.id, status);
      toast.success(status === 'completed' ? 'Marked as complete!' : 'Marked as skipped');
      onClose();
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };
  
  const eventColor = event.child?.color || event.activity?.color || '#5DADE2';
  const eventDate = parseISO(event.date);
  const isPast = eventDate < new Date();
  const isCompleted = event.status === 'completed';
  const isSkipped = event.status === 'skipped';
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg mx-4 p-0 rounded-2xl overflow-hidden border-0 shadow-2xl [&>button]:hidden">
        {/* Header with gradient background */}
        <div 
          className="relative p-6 pb-8"
          style={{ 
            background: `linear-gradient(135deg, ${eventColor} 0%, ${eventColor}dd 100%)`,
          }}
        >
          {/* Close button */}
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors z-10"
          >
            <X className="h-4 w-4 text-white" />
          </button>
          
          {/* Activity icon */}
          <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center mb-4">
            <span className="text-2xl">{event.activity?.icon || '📅'}</span>
          </div>
          
          {/* Event title */}
          <h2 className="text-2xl font-bold text-white mb-1">{event.title}</h2>
          <p className="text-white/80 text-sm font-medium">
            {format(eventDate, 'EEEE, MMMM d, yyyy')}
          </p>
          
          {/* Status badge */}
          {(isCompleted || isSkipped) && (
            <div className={`absolute bottom-4 right-4 px-3 py-1.5 rounded-full flex items-center gap-1.5 text-xs font-semibold ${
              isCompleted ? 'bg-green-500 text-white' : 'bg-orange-500 text-white'
            }`}>
              {isCompleted ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
              {isCompleted ? 'Completed' : 'Skipped'}
            </div>
          )}
        </div>
        
        {/* Content */}
        <div className="p-6 space-y-4 bg-white">
          {/* Time & Duration */}
          <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-gray-50 to-gray-100/50 rounded-xl">
            <div className="w-12 h-12 rounded-xl bg-white shadow-sm flex items-center justify-center">
              <Clock className="h-6 w-6 text-violet-500" />
            </div>
            <div className="flex-1">
              <p className="text-lg font-semibold text-gray-900">
                {event.start_time} – {event.end_time}
              </p>
              <p className="text-sm text-gray-500">Duration</p>
            </div>
          </div>
          
          {/* Location */}
          {event.location && (
            <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-gray-50 to-gray-100/50 rounded-xl">
              <div className="w-12 h-12 rounded-xl bg-white shadow-sm flex items-center justify-center">
                <MapPin className="h-6 w-6 text-rose-500" />
              </div>
              <div className="flex-1">
                <p className="text-lg font-semibold text-gray-900">{event.location}</p>
                <p className="text-sm text-gray-500">Location</p>
              </div>
            </div>
          )}
          
          {/* Child */}
          {event.child && (
            <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-gray-50 to-gray-100/50 rounded-xl">
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-sm"
                style={{ backgroundColor: event.child.color }}
              >
                {event.child.name?.charAt(0)}
              </div>
              <div className="flex-1">
                <p className="text-lg font-semibold text-gray-900">{event.child.name}</p>
                <p className="text-sm text-gray-500">Participant</p>
              </div>
            </div>
          )}
          
          {/* Notes */}
          {event.notes && (
            <div className="p-4 bg-amber-50 border border-amber-100 rounded-xl">
              <p className="text-xs font-medium text-amber-600 uppercase tracking-wider mb-1">Notes</p>
              <p className="text-sm text-gray-700">{event.notes}</p>
            </div>
          )}
        </div>
        
        {/* Footer Actions */}
        <div className="p-4 bg-gray-50 border-t border-gray-100 space-y-3">
          {/* Status buttons */}
          {(!event.status || event.status === 'pending' || event.status === 'scheduled') && (
            <div className="flex gap-3">
              <Button
                onClick={() => handleStatusChange('completed')}
                disabled={updating}
                className="flex-1 h-12 rounded-xl bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white hover:text-white font-semibold shadow-lg shadow-green-500/25 transition-all"
              >
                <CheckCircle2 className="mr-2 h-5 w-5" />
                Mark Complete
              </Button>
              <Button
                onClick={() => handleStatusChange('skipped')}
                disabled={updating}
                variant="outline"
                className="flex-1 h-12 rounded-xl border-2 border-orange-200 text-orange-600 hover:text-orange-700 hover:bg-orange-50 hover:border-orange-300 font-semibold transition-all"
              >
                <XCircle className="mr-2 h-5 w-5" />
                Skip
              </Button>
            </div>
          )}
          
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              onClick={() => onEdit(event)} 
              className="flex-1 h-11 rounded-xl border-2 border-gray-200 text-gray-700 hover:text-gray-900 hover:border-gray-300 hover:bg-gray-100 font-medium transition-all"
            >
              <Edit2 className="mr-2 h-4 w-4" />
              Edit Event
            </Button>
            <Button 
              variant="ghost" 
              onClick={onClose} 
              className="h-11 px-6 rounded-xl text-gray-600 hover:text-gray-900 hover:bg-gray-100 font-medium transition-all"
            >
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Markdown Summary Component with enhanced formatting
const MarkdownSummary = ({ content }) => {
  if (!content) return null;
  
  return (
    <div className="space-y-2">
      <ReactMarkdown
        components={{
          // Make header lines large and bold
          p: ({ node, children, ...props }) => {
            const text = String(children);
            // Check if this looks like a header line (contains "Week at a Glance" or "at a Glance")
            if (text.includes('at a Glance') || text.includes('Week at a Glance')) {
              return (
                <h3 className="text-xl font-bold text-gray-900 mb-1" {...props}>
                  {children}
                </h3>
              );
            }
            // Check if this is the subheader (X events across Y days)
            if (text.includes('events across') && text.includes('day')) {
              return (
                <p className="text-base text-gray-500 mb-4 pb-3 border-b-2 border-violet-100" {...props}>
                  {children}
                </p>
              );
            }
            // Check if line starts with day abbreviation (Mon, Tue, Wed, etc.) - these are date headers
            if (/^(Mon|Tue|Wed|Thu|Fri|Sat|Sun)\s+(\d{4}-\d{2}-\d{2}|\d{1,2}\/\d{1,2})/.test(text)) {
              // Split the date from the events
              const parts = text.split(/\s*[·•]\s*/);
              const dateHeader = parts[0];
              const events = parts.slice(1);
              
              return (
                <div className="mt-3 first:mt-0" {...props}>
                  <div className="flex items-center mb-1.5">
                    <span className="inline-block w-2.5 h-2.5 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 mr-2.5"></span>
                    <span className="font-semibold text-gray-900">{dateHeader}</span>
                  </div>
                  {events.length > 0 && (
                    <div className="ml-5 pl-3 border-l-2 border-violet-100 space-y-0.5">
                      {events.map((event, i) => (
                        <p key={i} className="text-gray-600 text-sm leading-relaxed py-0.5">{event}</p>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            // Check if this paragraph contains "Heads Up" (possibly bold) - this is a tips/notes header
            // When children contains a React element (like <strong>Heads Up</strong>), text will include "[object Object]"
            const hasReactElement = text.includes('[object Object]');
            const nodeText = node?.children?.map(c => c.value || c.children?.[0]?.value || '').join('') || '';
            const isHeadsUp = text.includes('Heads Up') || text.includes('💡') || 
              nodeText.includes('Heads Up') ||
              (hasReactElement && node?.children?.some(child => 
                child.type === 'strong' && child.children?.[0]?.value?.includes?.('Heads Up')
              ));
            
            if (isHeadsUp) {
              return (
                <div className="mt-5 pt-4 border-t-2 border-violet-200" {...props}>
                  <div className="flex items-start bg-amber-50/70 p-3 rounded-lg text-gray-700">
                    <span className="mr-2 text-lg flex-shrink-0">💡</span>
                    <div className="font-semibold">{children}</div>
                  </div>
                </div>
              );
            }
            // Check for Note or Tip sections
            if (text.includes('Note:') || text.includes('Tip:')) {
              return (
                <div className="mt-4 pt-4 border-t-2 border-violet-100" {...props}>
                  <div className="p-3 bg-gradient-to-r from-amber-50 to-orange-50 border-l-4 border-amber-400 rounded-r-lg">
                    <p className="text-amber-800 text-sm font-medium flex items-start">
                      <span className="mr-2 mt-0.5">💡</span>
                      <span>{children}</span>
                    </p>
                  </div>
                </div>
              );
            }
            return <p className="text-gray-600 leading-relaxed my-1" {...props}>{children}</p>;
          },
          strong: ({ node, children, ...props }) => {
            const text = String(children);
            // Check if this is "Heads Up" - style it with bold
            if (text.includes('Heads Up') || text.match(/^Heads\s*Up$/i)) {
              return <strong className="font-bold text-gray-900" {...props}>{children}</strong>;
            }
            return <strong className="font-semibold text-gray-900" {...props}>{children}</strong>;
          },
          ul: ({ node, children, ...props }) => (
            <ul className="space-y-1 ml-5 pl-3 border-l-2 border-violet-100 my-2" {...props}>{children}</ul>
          ),
          li: ({ node, children, ...props }) => (
            <li className="text-gray-600 text-sm py-0.5" {...props}>
              {children}
            </li>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

// Daily Agenda View Component
const DailyAgendaView = ({ events, date, onEventClick, allEvents, onNavigateToDate }) => {
  const timeSlots = [];
  for (let hour = 6; hour <= 22; hour++) {
    timeSlots.push(hour);
  }

  const eventsByHour = useMemo(() => {
    const grouped = {};
    events.forEach(event => {
      if (event.start_time) {
        const hour = parseInt(event.start_time.split(':')[0], 10);
        if (!grouped[hour]) grouped[hour] = [];
        grouped[hour].push(event);
      }
    });
    return grouped;
  }, [events]);

  // Find the next day with events
  const nextEventDay = useMemo(() => {
    if (!allEvents || allEvents.length === 0) return null;
    
    const currentDateStr = format(date, 'yyyy-MM-dd');
    
    // Get all future events sorted by date
    const futureEvents = allEvents
      .filter(event => event.date > currentDateStr)
      .sort((a, b) => a.date.localeCompare(b.date));
    
    if (futureEvents.length === 0) return null;
    
    const nextDate = futureEvents[0].date;
    const eventsOnNextDate = futureEvents.filter(e => e.date === nextDate);
    
    // Parse the date string to create a date object
    const [year, month, day] = nextDate.split('-').map(Number);
    const parsedDate = new Date(year, month - 1, day);
    
    return {
      date: parsedDate,
      dateStr: nextDate,
      events: eventsOnNextDate,
      count: eventsOnNextDate.length
    };
  }, [allEvents, date]);

  const formatHour = (hour) => {
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour} ${period}`;
  };

  const hasEvents = events.length > 0;

  // If no events, show prominent empty state
  if (!hasEvents) {
    return (
      <div className="space-y-1">
        <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-100">
          <div>
            <h3 className="font-bold text-gray-900">{format(date, 'EEEE')}</h3>
            <p className="text-sm text-gray-500">{format(date, 'MMMM d, yyyy')}</p>
          </div>
          <div className="px-3 py-1 rounded-lg text-sm font-medium bg-gray-100 text-gray-500">
            0 events
          </div>
        </div>

        {/* Prominent Empty State */}
        <div className="flex flex-col items-center justify-center py-12 px-4">
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center mb-4">
            <CalendarDays className="h-10 w-10 text-gray-400" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">No Events Today</h3>
          <p className="text-gray-500 text-center mb-6 max-w-xs">
            Enjoy your free day! Nothing scheduled for {format(date, 'EEEE, MMMM d')}.
          </p>
          
          {/* Next Event Card */}
          {nextEventDay && (
            <div className="w-full max-w-sm bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-4 border border-blue-100">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center">
                  <ChevronRight className="h-4 w-4 text-white" />
                </div>
                <div>
                  <p className="text-xs font-medium text-blue-600 uppercase tracking-wider">Next Event</p>
                  <p className="font-semibold text-gray-900">{format(nextEventDay.date, 'EEEE, MMM d')}</p>
                </div>
              </div>
              
              <div className="space-y-2 mb-4">
                {nextEventDay.events.slice(0, 3).map((event, idx) => {
                  const eventColor = event.child?.color || event.activity?.color || '#5DADE2';
                  return (
                    <div 
                      key={event.id || idx}
                      className="flex items-center gap-2 bg-white/70 rounded-lg px-3 py-2"
                    >
                      <div 
                        className="w-2 h-2 rounded-full flex-shrink-0" 
                        style={{ backgroundColor: eventColor }} 
                      />
                      <span className="text-sm text-gray-700 truncate flex-1">{event.title}</span>
                      <span className="text-xs text-gray-500">{event.start_time}</span>
                    </div>
                  );
                })}
                {nextEventDay.count > 3 && (
                  <p className="text-xs text-gray-500 text-center">+{nextEventDay.count - 3} more event{nextEventDay.count - 3 > 1 ? 's' : ''}</p>
                )}
              </div>
              
              <Button
                onClick={() => onNavigateToDate && onNavigateToDate(nextEventDay.date)}
                className="w-full rounded-xl bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white shadow-md shadow-blue-500/20"
                data-testid="jump-to-next-event-btn"
              >
                <CalendarCheck className="mr-2 h-4 w-4" />
                Jump to {format(nextEventDay.date, 'MMM d')}
              </Button>
            </div>
          )}
          
          {/* No Future Events */}
          {!nextEventDay && (
            <div className="text-center">
              <p className="text-sm text-gray-400">No upcoming events scheduled</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-100">
        <div>
          <h3 className="font-bold text-gray-900">{format(date, 'EEEE')}</h3>
          <p className="text-sm text-gray-500">{format(date, 'MMMM d, yyyy')}</p>
        </div>
        <div className={`px-3 py-1 rounded-lg text-sm font-medium ${
          hasEvents ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-500'
        }`}>
          {events.length} event{events.length !== 1 ? 's' : ''}
        </div>
      </div>

      <div className="relative">
        {timeSlots.map((hour) => {
          const hourEvents = eventsByHour[hour] || [];
          const hasHourEvents = hourEvents.length > 0;

          return (
            <div key={hour} className="flex min-h-[48px]">
              <div className="w-16 flex-shrink-0 text-xs text-gray-400 pt-1 text-right pr-3">
                {formatHour(hour)}
              </div>
              
              <div className="flex-1 relative border-l border-gray-200 pl-4">
                <div className={`absolute -left-1 top-1.5 w-2 h-2 rounded-full ${
                  hasHourEvents ? 'bg-blue-500' : 'bg-gray-200'
                }`} />
                
                {hourEvents.map((event, idx) => {
                  const eventColor = event.child?.color || event.activity?.color || '#5DADE2';
                  return (
                    <div 
                      key={event.id || idx}
                      onClick={() => onEventClick(event)}
                      className="mb-2 p-3 rounded-xl bg-white border border-gray-100 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                      style={{ borderLeftWidth: '3px', borderLeftColor: eventColor }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="font-medium text-gray-900 text-sm">{event.title}</div>
                        {event.status === 'completed' && <CheckCircle2 className="h-4 w-4 text-green-500" />}
                        {event.status === 'skipped' && <XCircle className="h-4 w-4 text-orange-500" />}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {event.start_time}{event.end_time ? ` - ${event.end_time}` : ''}
                        </span>
                        {event.location && (
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {event.location}
                          </span>
                        )}
                      </div>
                      {event.child && (
                        <div className="mt-2 flex items-center gap-1.5">
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: event.child.color }} />
                          <span className="text-xs text-gray-500">{event.child.name}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [allEvents, setAllEvents] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  
  const [timeFilter, setTimeFilter] = useState('today');
  const [selectedKids, setSelectedKids] = useState([]);
  const [kidsDropdownOpen, setKidsDropdownOpen] = useState(false);
  
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [lastEventsModified, setLastEventsModified] = useState(null);
  
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventModalOpen, setEventModalOpen] = useState(false);

  useEffect(() => {
    loadDashboard();
    const handleFocus = () => {
      loadDashboard();
      // BUG-4: Check if events were modified while away and refresh summary
      checkForEventChanges();
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  // BUG-4: Check if events have been modified elsewhere and refresh summary
  const checkForEventChanges = () => {
    const storedTimestamp = localStorage.getItem('eventsLastModified');
    if (storedTimestamp && storedTimestamp !== lastEventsModified) {
      setLastEventsModified(storedTimestamp);
      setSummary(null); // Clear stale summary
      // Summary will be regenerated by the useEffect below
    }
  };

  useEffect(() => {
    if (data && !loading) {
      generateSummary();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeFilter, selectedKids, lastEventsModified]);

  const loadDashboard = async () => {
    try {
      const [dashboardData, eventsData] = await Promise.all([
        fetchDashboard(),
        fetchEvents()
      ]);
      setData(dashboardData);
      setAllEvents(eventsData || []);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const generateSummary = async () => {
    setSummaryLoading(true);
    try {
      let result;
      const dateStr = format(currentDate, 'yyyy-MM-dd');
      
      // BUG-3 FIX: Use appropriate summary endpoint based on time filter
      if (timeFilter === 'month') {
        result = await getMonthlySummary(dateStr);
      } else if (timeFilter === 'week') {
        result = await getWeeklySummary(dateStr);
      } else {
        result = await getDailySummary(dateStr);
      }
      
      setSummary(result);
    } catch (error) {
      toast.error('Failed to generate summary');
    } finally {
      setSummaryLoading(false);
    }
  };

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setEventModalOpen(true);
  };

  const handleStatusUpdate = async (eventId, status) => {
    const updated = await updateEventStatus(eventId, status);
    setAllEvents(prev => prev.map(e => e.id === eventId ? updated : e));
    return updated;
  };

  const handleEditEvent = (event) => {
    setEventModalOpen(false);
    navigate(`/family?tab=events&edit=${event.id}`);
  };

  const toggleKidSelection = (kidId) => {
    setSelectedKids(prev => {
      if (prev.includes(kidId)) {
        return prev.filter(id => id !== kidId);
      } else {
        return [...prev, kidId];
      }
    });
  };

  const selectAllKids = () => {
    if (data?.family_members) {
      setSelectedKids(data.family_members.map(m => m.id));
    }
  };

  const clearKidSelection = () => {
    setSelectedKids([]);
  };

  // Helper to parse date string to local date
  const parseEventDate = (dateStr) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  // Filter events by time and selected kids
  const filteredEvents = useMemo(() => {
    let events = allEvents;
    
    // Filter by selected kids (if any selected)
    if (selectedKids.length > 0) {
      events = events.filter(e => selectedKids.includes(e.child_id));
    }
    
    // Filter by time range based on currentDate
    events = events.filter(event => {
      const eventDate = parseEventDate(event.date);
      
      if (timeFilter === 'today') {
        return isSameDay(eventDate, currentDate);
      } else if (timeFilter === 'week') {
        const weekStart = startOfWeek(currentDate);
        const weekEnd = endOfWeek(currentDate);
        return eventDate >= weekStart && eventDate <= weekEnd;
      } else if (timeFilter === 'month') {
        return isSameMonth(eventDate, currentDate);
      }
      return true;
    });
    
    // Sort by date then time
    events.sort((a, b) => {
      const dateCompare = a.date.localeCompare(b.date);
      if (dateCompare !== 0) return dateCompare;
      if (a.start_time && b.start_time) {
        return a.start_time.localeCompare(b.start_time);
      }
      return 0;
    });
    
    return events;
  }, [allEvents, selectedKids, timeFilter, currentDate]);

  // Events grouped by date for calendar
  const eventsByDate = useMemo(() => {
    const groups = {};
    const eventsForCalendar = selectedKids.length > 0
      ? allEvents.filter(e => selectedKids.includes(e.child_id))
      : allEvents;
    
    eventsForCalendar.forEach(event => {
      if (!groups[event.date]) groups[event.date] = [];
      groups[event.date].push(event);
    });
    return groups;
  }, [allEvents, selectedKids]);

  // Calendar days
  const calendarDays = useMemo(() => {
    if (timeFilter === 'week') {
      return eachDayOfInterval({ 
        start: startOfWeek(currentDate), 
        end: endOfWeek(currentDate) 
      });
    } else {
      const monthStart = startOfMonth(currentDate);
      const monthEnd = endOfMonth(currentDate);
      const calendarStart = startOfWeek(monthStart);
      const calendarEnd = endOfWeek(monthEnd);
      return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
    }
  }, [currentDate, timeFilter]);

  const handlePrev = () => {
    if (timeFilter === 'today') {
      setCurrentDate(prev => subDays(prev, 1));
    } else if (timeFilter === 'week') {
      setCurrentDate(prev => subWeeks(prev, 1));
    } else {
      setCurrentDate(prev => subMonths(prev, 1));
    }
  };

  const handleNext = () => {
    if (timeFilter === 'today') {
      setCurrentDate(prev => addDays(prev, 1));
    } else if (timeFilter === 'week') {
      setCurrentDate(prev => addWeeks(prev, 1));
    } else {
      setCurrentDate(prev => addMonths(prev, 1));
    }
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const getSummaryTitle = () => {
    switch (timeFilter) {
      case 'today':
        return "Today's Summary";
      case 'week':
        return "This Week";
      case 'month':
        return "This Month";
      default:
        return "Summary";
    }
  };

  const getCalendarTitle = () => {
    if (timeFilter === 'today') {
      return format(currentDate, 'MMMM d, yyyy');
    } else if (timeFilter === 'week') {
      const weekStart = startOfWeek(currentDate);
      const weekEnd = endOfWeek(currentDate);
      return `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d, yyyy')}`;
    }
    return format(currentDate, 'MMMM yyyy');
  };

  const getKidFilterLabel = () => {
    if (selectedKids.length === 0) {
      return 'All Kids';
    } else if (selectedKids.length === 1) {
      const kid = data?.family_members?.find(m => m.id === selectedKids[0]);
      return kid?.name || '1 Kid';
    } else {
      return `${selectedKids.length} Kids`;
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-12 rounded-xl" />
        <Skeleton className="h-20 rounded-2xl" />
        <Skeleton className="h-64 rounded-2xl" />
      </div>
    );
  }

  const { family_members } = data || {};
  const selectedKidObjects = family_members?.filter(m => selectedKids.includes(m.id)) || [];

  return (
    <div className="min-h-screen pb-24 md:pb-8">
      {/* Header */}
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-sm text-gray-500">{format(new Date(), 'EEEE, MMMM d, yyyy')}</p>
      </div>

      {/* Quick Filters */}
      <div className="mb-4 space-y-3">
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-4 px-4 md:mx-0 md:px-0">
          <TimeChip 
            icon={CalendarCheck} 
            label="Today" 
            isActive={timeFilter === 'today'} 
            onClick={() => { setTimeFilter('today'); setCurrentDate(new Date()); }} 
          />
          <TimeChip 
            icon={CalendarRange} 
            label="Week" 
            isActive={timeFilter === 'week'} 
            onClick={() => { setTimeFilter('week'); setCurrentDate(new Date()); }} 
          />
          <TimeChip 
            icon={Calendar} 
            label="Month" 
            isActive={timeFilter === 'month'} 
            onClick={() => { setTimeFilter('month'); setCurrentDate(new Date()); }} 
          />
          
          {family_members?.length > 0 && (
            <Popover open={kidsDropdownOpen} onOpenChange={setKidsDropdownOpen}>
              <PopoverTrigger asChild>
                <button className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium bg-white border border-gray-200 hover:border-gray-300 transition-all whitespace-nowrap">
                  {selectedKids.length > 0 && selectedKids.length <= 2 ? (
                    <div className="flex -space-x-1">
                      {selectedKidObjects.slice(0, 2).map(kid => (
                        <div 
                          key={kid.id}
                          className="w-4 h-4 rounded-full border-2 border-white" 
                          style={{ backgroundColor: kid.color }} 
                        />
                      ))}
                    </div>
                  ) : (
                    <Users className="h-4 w-4" />
                  )}
                  <span>{getKidFilterLabel()}</span>
                  <ChevronDown className="h-3 w-3 text-gray-400" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-56 p-2" align="start">
                <div className="space-y-1">
                  <div className="flex gap-2 pb-2 mb-2 border-b border-gray-100">
                    <button 
                      onClick={selectAllKids}
                      className="flex-1 text-xs text-blue-600 hover:text-blue-700 py-1"
                    >
                      Select All
                    </button>
                    <button 
                      onClick={clearKidSelection}
                      className="flex-1 text-xs text-gray-500 hover:text-gray-700 py-1"
                    >
                      Clear
                    </button>
                  </div>
                  
                  {family_members.map(kid => (
                    <button
                      key={kid.id}
                      onClick={() => toggleKidSelection(kid.id)}
                      className="w-full flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <div 
                        className={`w-5 h-5 rounded flex items-center justify-center border-2 transition-colors ${
                          selectedKids.includes(kid.id) 
                            ? 'border-blue-500 bg-blue-500' 
                            : 'border-gray-300'
                        }`}
                      >
                        {selectedKids.includes(kid.id) && (
                          <Check className="h-3 w-3 text-white" />
                        )}
                      </div>
                      <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: kid.color }}>
                        {kid.name?.charAt(0)}
                      </div>
                      <span className="text-sm text-gray-700">{kid.name}</span>
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          )}
        </div>

        {selectedKids.length > 0 && (
          <div className="flex gap-2 flex-wrap">
            {selectedKidObjects.map(kid => (
              <button
                key={kid.id}
                onClick={() => toggleKidSelection(kid.id)}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium text-white transition-all hover:opacity-80"
                style={{ backgroundColor: kid.color }}
              >
                {kid.name}
                <span className="ml-1 opacity-70">×</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Main Content - Stacked layout */}
      <div className="space-y-6">

        {/* AI Summary Widget - Full width */}
        <FloatingWidget className="p-4 md:p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                <Sparkles className="h-5 w-5 md:h-6 md:w-6 text-white" />
              </div>
              <div>
                <span className="font-semibold text-gray-900">{getSummaryTitle()}</span>
                {selectedKids.length > 0 && (
                  <p className="text-sm text-gray-500">
                    for {selectedKids.length === 1 
                      ? selectedKidObjects[0]?.name 
                      : `${selectedKids.length} kids`}
                  </p>
                )}
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={generateSummary}
              disabled={summaryLoading}
              className="rounded-xl"
            >
              {summaryLoading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              {summary ? 'Refresh' : 'Generate'}
            </Button>
          </div>
          <div className="text-sm md:text-base leading-relaxed">
            {summary ? (
              <MarkdownSummary content={summary.summary} />
            ) : (
              <p className="text-gray-400">Click "Generate" for {timeFilter === 'today' ? "today's" : timeFilter === 'week' ? "this week's" : "this month's"} AI summary</p>
            )}
          </div>
        </FloatingWidget>

        {/* Calendar / Daily Agenda Widget - Full width */}
        <FloatingWidget className="p-4 md:p-6 overflow-hidden">
          {/* Navigation Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={handlePrev}
                className="h-8 w-8 rounded-xl"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <h2 className="text-sm font-bold text-gray-900 min-w-[160px] text-center">
                {getCalendarTitle()}
              </h2>
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={handleNext}
                className="h-8 w-8 rounded-xl"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            {!isSameDay(currentDate, new Date()) && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleToday}
                className="rounded-xl text-xs h-8"
              >
                Today
              </Button>
            )}
          </div>

          {/* Daily Agenda View for Today Filter */}
          {timeFilter === 'today' ? (
            <DailyAgendaView 
              events={filteredEvents} 
              date={currentDate} 
              onEventClick={handleEventClick}
              allEvents={allEvents}
              onNavigateToDate={(newDate) => setCurrentDate(newDate)}
            />
          ) : (
            <>
              {/* Calendar Grid for Week/Month */}
              <div className="space-y-1">
                <div className="grid grid-cols-7 gap-0.5">
                  {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
                    <div key={i} className="text-center text-[10px] font-semibold text-gray-400 py-1">
                      {day}
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-0.5">
                  {calendarDays.map(day => {
                    const dateStr = format(day, 'yyyy-MM-dd');
                    const dayEvents = eventsByDate[dateStr] || [];
                    const isCurrentMonth = timeFilter === 'week' || isSameMonth(day, currentDate);
                    const today = isToday(day);

                    return (
                      <div
                        key={day.toISOString()}
                        onClick={() => {
                          if (dayEvents.length === 1) {
                            handleEventClick(dayEvents[0]);
                          } else if (dayEvents.length > 1) {
                            setTimeFilter('today');
                            setCurrentDate(day);
                          }
                        }}
                        className={`min-h-[80px] p-1 rounded-lg transition-all cursor-pointer hover:bg-gray-50 border border-gray-100 ${
                          !isCurrentMonth ? 'opacity-30 bg-gray-50/50' : 'bg-white'
                        } ${today ? 'bg-blue-50 ring-2 ring-blue-300 border-blue-200' : ''}`}
                      >
                        <div className={`text-xs font-semibold mb-1 ${
                          today ? 'text-blue-600' : 'text-gray-700'
                        }`}>
                          {format(day, 'd')}
                        </div>
                        {dayEvents.length > 0 && (
                          <div className="space-y-0.5 overflow-hidden">
                            {dayEvents.slice(0, 3).map((event, i) => {
                              const eventColor = event.child?.color || event.activity?.color || '#5DADE2';
                              return (
                                <div
                                  key={i}
                                  className="flex items-center gap-1 px-1 py-0.5 rounded text-[9px] truncate"
                                  style={{ 
                                    backgroundColor: `${eventColor}20`,
                                    borderLeft: `2px solid ${eventColor}`
                                  }}
                                  title={`${event.title} - ${event.start_time}`}
                                >
                                  <span className="font-medium text-gray-700 truncate">{event.start_time?.slice(0, 5)}</span>
                                  <span className="text-gray-600 truncate flex-1">{event.title?.length > 12 ? event.title.slice(0, 12) + '...' : event.title}</span>
                                </div>
                              );
                            })}
                            {dayEvents.length > 3 && (
                              <div className="text-[9px] text-gray-400 text-center font-medium">
                                +{dayEvents.length - 3} more
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Events List for Week/Month */}
              {filteredEvents.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <h3 className="font-semibold text-gray-900 text-sm mb-3">
                    {timeFilter === 'week' ? "This Week's Events" : "This Month's Events"}
                    <span className="ml-2 text-gray-400 font-normal">({filteredEvents.length})</span>
                  </h3>
                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                    {filteredEvents.slice(0, 10).map(event => {
                      const eventColor = event.child?.color || event.activity?.color || '#5DADE2';
                      return (
                        <div 
                          key={event.id} 
                          onClick={() => handleEventClick(event)}
                          className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                        >
                          <div className="w-1 h-8 rounded-full flex-shrink-0" style={{ backgroundColor: eventColor }} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-gray-900 text-xs truncate">{event.title}</p>
                              {event.status === 'completed' && <CheckCircle2 className="h-3 w-3 text-green-500 flex-shrink-0" />}
                              {event.status === 'skipped' && <XCircle className="h-3 w-3 text-orange-500 flex-shrink-0" />}
                            </div>
                            <p className="text-[10px] text-gray-500">
                              {format(parseEventDate(event.date), 'EEE, MMM d')} • {event.start_time}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                    {filteredEvents.length > 10 && (
                      <p className="text-xs text-center text-gray-400 pt-1">+{filteredEvents.length - 10} more</p>
                    )}
                  </div>
                </div>
              )}

              {/* Legend */}
              {family_members?.length > 0 && (
                <div className="flex items-center gap-3 mt-3 pt-3 border-t border-gray-100 overflow-x-auto">
                  {family_members.map(kid => (
                    <div key={kid.id} className="flex items-center gap-1.5 flex-shrink-0">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: kid.color }} />
                      <span className="text-xs text-gray-500">{kid.name}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </FloatingWidget>
      </div>

      {/* Empty State */}
      {!family_members?.length && !allEvents?.length && (
        <FloatingWidget className="p-8 text-center mt-4">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center">
            <CalendarDays className="h-8 w-8 text-blue-500" />
          </div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">Welcome to Unified!</h3>
          <p className="text-sm text-gray-500 mb-5">
            Start by adding your kids and their activities.
          </p>
          <Button 
            onClick={() => navigate('/family')}
            className="rounded-xl w-full"
            style={{ background: 'linear-gradient(135deg, #5DADE2, #8E44AD)' }}
          >
            <Users className="mr-2 h-4 w-4" />
            Set up My Family
          </Button>
        </FloatingWidget>
      )}

      {/* Event Detail Modal */}
      <EventDetailModal
        event={selectedEvent}
        open={eventModalOpen}
        onClose={() => setEventModalOpen(false)}
        onStatusUpdate={handleStatusUpdate}
        onEdit={handleEditEvent}
      />
    </div>
  );
};

export default Dashboard;
